import { html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Selector, SelectorEventDetail, selectorsToString } from "../rules";
import { OutDesignSelectorEventDispatcherElement } from "./SelectorEventDispatcher";
import { parseSelectorList } from "../rules/selectors";

@customElement("od-selector")
export class OutDesignSelectorElement extends OutDesignSelectorEventDispatcherElement {
    @state() selectors : Selector[] = [{}] ;
    @state() bufferSelector: string = "";
    @state() private editing = false;
    @property({ type: Boolean, reflect: true}) private flashing: boolean;

    static styles = css`
    form {
        display: flex;
        align-items: flex-end ;
        gap: 0.5em;
    }

    wa-input {
        flex: 1 auto;
        flex-direction: row;
        align-items: center;
        gap: 0.7em;
    }

    wa-input::part(label) {
        margin-top: 0.5em;
    }

    :host([flashing]) wa-input::part(base) {
        outline: var(--wa-focus-ring);
        outline-offset: var(--wa-focus-ring-offset);
    }
    `

    private handleSelectorAdded = (e: Event) => {
        const ev = e as CustomEvent<SelectorEventDetail>;
        this.updateSelectors(ev, false);
        this.flash()
    }

    private handleTempSelectorAdded = (e: Event) => {
        const ev = e as CustomEvent<SelectorEventDetail>;
        this.updateSelectors(ev, true);
    }

    private handleOperatorReceived = (e: Event) => {
        const ev = e as CustomEvent<string>;
        const operator = ev.detail;

        this.emitNewRule(this.selectors, operator);
    };

    private flash() {
        const flashDuration = 400;
        this.flashing= true ;
        setTimeout(() => {
            this.flashing = false;
        }, flashDuration);
    }

    connectedCallback() {
        super.connectedCallback()
        document.addEventListener("selector-added", this.handleSelectorAdded);
        document.addEventListener("temp-selector-added", this.handleTempSelectorAdded);
        document.addEventListener("new-rule-with-operator", this.handleOperatorReceived);
    }

    disconnectedCallback(): void {
        document.removeEventListener("selector-added", this.handleSelectorAdded);
        document.removeEventListener("temp-selector-added", this.handleTempSelectorAdded);
        document.removeEventListener("new-rule-with-operator", this.handleOperatorReceived);
        super.disconnectedCallback()
    }

    private updateSelectors(e: CustomEvent<SelectorEventDetail>, temp: boolean) {
        const parts = e.detail.part ?? [];

        if (temp) return;

        if (e.detail.mode === "replace") {
            this.selectors = parts;
        } else {
            this.selectors = [...this.selectors, ...parts];
        }

        if (!this.editing) {
            this.bufferSelector = selectorsToString(this.selectors);
        }
    }

    private emitNewRule(selectors: Array<Partial<Selector>>, operator?: string) {
        this.dispatchEvent(new CustomEvent("new-rule", {
            detail: {
                selectors: selectors,
                operator: operator
            },
            bubbles: true,
            composed: true
        }));
    }

    private updateBufferSelector(newRawSel : string): void {
        this.bufferSelector = newRawSel;
        this.emit(parseSelectorList(this.bufferSelector), "replace");
    }

    protected render() {
        return html`
            <form>
                <wa-input
                label="Selector"
                size="small"
                with-clear
                placeholder=".my-class"
                .value=${this.bufferSelector ?? ""}
                @input=${(e: any) => this.updateBufferSelector(e.target.value)}
                @focus=${() => this.editing = true}
                @blur=${() => this.editing = false}
                ></wa-input>
                <wa-button
                    size="small"
                    appearance="outlined"
                    @click=${() => this.emitNewRule(this.selectors)}>
                    New rule</wa-button>
            </form>
        `;
    }
}